import React from 'react';
import { CheckIcon, PuzzlePieceIcon, UserIcon } from '@heroicons/react/24/outline';
import { GameOutput } from '../../generated-api';

interface GameCardProps {
  game: GameOutput;
  isSelected: boolean;
  onSelect: (gameId: string) => void;
  lastPlayedAt?: string;
}

function formatRelativeTime(dateStr: string): string {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return '1d ago';
  if (diffDays < 7) return `${diffDays}d ago`;
  const diffWeeks = Math.floor(diffDays / 7);
  if (diffWeeks < 5) return `${diffWeeks}w ago`;
  const diffMonths = Math.floor(diffDays / 30);
  if (diffMonths < 12) return `${diffMonths}mo ago`;
  const diffYears = Math.floor(diffDays / 365);
  return `${diffYears}y ago`;
}

function formatPlayerCount(min: number, max: number): string {
  if (min === max) return `${min}p`;
  return `${min}-${max}p`;
}

const GameCard: React.FC<GameCardProps> = ({ game, isSelected, onSelect, lastPlayedAt }) => {
  const playerCount = formatPlayerCount(game.minTeams, game.maxTeams);
  const owners = game.owners?.join(', ');

  const detailParts: string[] = [];
  if (lastPlayedAt) detailParts.push(formatRelativeTime(lastPlayedAt));
  if (owners) detailParts.push(owners);

  return (
    <button
      data-track="wizard_game_card"
      type="button"
      onClick={() => onSelect(game.id)}
      data-testid={`wizard-game-${game.id}`}
      className={`relative flex flex-col items-center rounded-xl
                  border transition-all duration-150
                  active:scale-[0.97]
                  motion-reduce:active:scale-100
                  w-full
                  ${isSelected
                    ? 'ring-2 ring-primary-500 ring-offset-2 ring-offset-[var(--bg-base)] border-primary-300 bg-surface shadow-sm'
                    : 'border-edge bg-surface hover:border-edge-input hover:shadow-sm'
                  }`}
      aria-pressed={isSelected}
    >
      {/* Game image */}
      <div className="w-full aspect-square rounded-t-xl overflow-hidden bg-surface-subtle">
        {game.imageUrl ? (
          <img
            src={game.imageUrl}
            alt=""
            className="w-full h-full object-cover"
            loading="eager"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center
                          bg-gradient-to-br from-primary-50 to-indigo-50
                          dark:from-primary-900/20 dark:to-indigo-900/20">
            <PuzzlePieceIcon className="w-10 h-10 text-primary-300" />
          </div>
        )}
      </div>

      {/* Game info */}
      <div className="px-2 py-2 w-full space-y-0.5">
        <span className="text-sm font-medium text-heading line-clamp-1 text-center leading-tight block">
          {game.name}
        </span>
        <div className="flex items-center justify-center gap-1 text-[11px] text-muted">
          <UserIcon className="w-3 h-3 shrink-0" />
          <span>{playerCount}</span>
        </div>
        {detailParts.length > 0 && (
          <p className="text-[10px] text-disabled text-center truncate">
            {detailParts.join(' · ')}
          </p>
        )}
      </div>

      {/* Selection checkmark */}
      {isSelected && (
        <div className="absolute top-2 right-2 w-6 h-6 bg-primary-500 rounded-full
                        flex items-center justify-center shadow-sm">
          <CheckIcon className="w-4 h-4 text-white" strokeWidth={2.5} />
        </div>
      )}
    </button>
  );
};

export default GameCard;
